import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface LazyLoadSentinelProps {
  /**
   * Callback ref to attach to the sentinel element
   */
  sentinelRef: (node: HTMLDivElement | null) => void;

  /**
   * Whether currently loading
   */
  isLoading: boolean;

  /**
   * Whether there are more items to load
   */
  hasMore: boolean;

  /**
   * Optional loading text
   */
  loadingText?: string;

  /**
   * Optional end message when all items are loaded
   */
  endMessage?: string;
}

/**
 * Reusable LazyLoad Sentinel Component
 * Displays loading indicator or end message for infinite scroll
 *
 * @example
 * const sentinelRef = useInfiniteScroll({ ... });
 *
 * <LazyLoadSentinel
 *   sentinelRef={sentinelRef}
 *   isLoading={loading}
 *   hasMore={hasMore}
 * />
 */
export const LazyLoadSentinel: React.FC<LazyLoadSentinelProps> = ({
  sentinelRef,
  isLoading,
  hasMore,
  loadingText = 'Loading more...',
  endMessage,
}) => {
  return (
    <Box
      ref={sentinelRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
        minHeight: '60px',
        backgroundColor: 'rgba(0, 0, 0, 0.02)', // Subtle background to see the sentinel
      }}
    >
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            {loadingText}
          </Typography>
        </Box>
      )}
      {!isLoading && !hasMore && endMessage && (
        <Typography variant="body2" color="text.secondary">
          {endMessage}
        </Typography>
      )}
      {!isLoading && hasMore && (
        <Typography variant="body2" color="text.secondary">
          Scroll to load more...
        </Typography>
      )}
    </Box>
  );
};
